/**
 * Reading a mode key back into something a person can read.
 *
 * A personal best is stored under `encodeModeConfig`'s string —
 * `sprint:normal:lines-40` — and the server treats it as opaque on purpose
 * (`apps/server/src/accounts/model.ts`: "a new mode or a new axis needs no
 * migration"). That opacity is right for the store and wrong for a screen: the
 * admin panel and the account screen both show somebody a list of their bests,
 * and `sprint:normal:lines-40` is a key, not a label.
 *
 * So the decode lives here, on the client, next to the only two things that
 * can turn a `ModeConfig` into words — `buildMode(...).copy.name` and
 * `describeModeConfig` — and both of them come from `@interlock/modes`. That
 * is the whole point of doing it here rather than server-side: the label a
 * mode has is the mode's own business, the registry is where it lives, and the
 * server has no copy of it and should not grow one.
 *
 * ## What happens to a key this build does not understand
 *
 * It is shown as it is. A key written by a newer client, or by a mode that has
 * since been removed, still names a real result somebody achieved; refusing to
 * display it, or displaying "unknown", would lose information that the raw
 * string still carries. `parseModeKey` returns `null` and `describeModeKey`
 * falls back to the key itself.
 */
import { buildMode, describeModeConfig, isModeId, HARDNESS_IDS } from '@interlock/modes';
import type { Hardness, ModeConfig, SprintGoal } from '@interlock/modes';

/**
 * The `ModeConfig` a key encodes, or `null` when this build cannot read it.
 *
 * The inverse of `encodeModeConfig`, and deliberately strict: a segment that
 * is not a mode this build has, or not a hardness it knows, is not guessed at.
 */
export function parseModeKey(key: string): ModeConfig | null {
  const [modeId, hardness, goal, ...extra] = key.split(':');
  if (extra.length > 0) return null;
  if (modeId === undefined || !isModeId(modeId)) return null;
  if (hardness === undefined || !isHardness(hardness)) return null;
  if (goal === undefined) return { modeId, hardness };

  const parsed = parseGoal(goal);
  return parsed === null ? null : { modeId, hardness, goal: parsed };
}

/**
 * One line naming the mode and its configuration — "Sprint · Normal · 40
 * lines" — or the raw key when it cannot be read.
 */
export function describeModeKey(key: string): string {
  const config = parseModeKey(key);
  if (config === null) return key;
  return `${buildMode(config).copy.name} · ${describeModeConfig(config)}`;
}

function isHardness(value: string): value is Hardness {
  return (HARDNESS_IDS as readonly string[]).includes(value);
}

function parseGoal(segment: string): SprintGoal | null {
  const separator = segment.indexOf('-');
  if (separator < 1) return null;
  const kind = segment.slice(0, separator);
  const amount = Number.parseInt(segment.slice(separator + 1), 10);
  if (!Number.isFinite(amount) || amount <= 0) return null;
  if (kind === 'lines') return { kind: 'lines', lines: amount };
  if (kind === 'time') return { kind: 'time', ms: amount };
  return null;
}
